import React from "react";

const MONO = '"JetBrains Mono",ui-monospace,SFMono-Regular,Menlo,monospace';
const SERIF = '"Instrument Serif",Georgia,serif';
const ARABIC = "'Scheherazade New','Amiri',serif";

const DARK_PALETTE = {
  cardBg: "linear-gradient(170deg,#08070f 0%,#060510 45%,#07060e 100%)",
  cardBorder: "rgba(255,179,0,0.16)",
  cardShadow: "0 12px 56px rgba(0,0,0,0.85), 0 0 0 1px rgba(255,179,0,0.06), inset 0 1px 0 rgba(255,255,255,0.04)",
  patternStroke: "#ffb300",
  patternOpacity: 0.06,
  ambientGlow: "radial-gradient(circle, rgba(255,179,0,0.09) 0%, transparent 62%)",
  badgeBg: "rgba(255,255,255,0.06)",
  badgeBorder: "1px solid rgba(255,255,255,0.09)",
  badgeText: "rgba(255,255,255,0.65)",
  liveDot: "#ff4b4b",
  eyebrow: "rgba(255,179,0,0.4)",
  rosetteGold: "#ffb300",
  rosetteGoldShadow: "rgba(255,179,0,0.7), 0 0 60px rgba(255,179,0,0.3)",
  rosetteShadow: "drop-shadow(0 0 18px rgba(255,179,0,0.22))",
  stripBg: "rgba(0,0,0,0.5)",
  stripBorderPlaying: "rgba(255,179,0,0.1)",
  stripBorderBuffering: "rgba(255,179,0,0.2)",
  stripBorderError: "rgba(255,107,107,0.2)",
  stripBorderStopped: "rgba(255,255,255,0.05)",
  siteLink: "rgba(255,179,0,0.55)",
  showText: "rgba(255,255,255,0.85)",
  showArtist: "rgba(255,179,0,0.7)",
  waveformGradient: "linear-gradient(to top, rgba(255,179,0,0.44), #ffb300)",
  buttonPlayingBg: "linear-gradient(135deg,#ffb300 0%,#e09600 50%,#cc8000 100%)",
  buttonPlayingShadow: "0 0 28px rgba(255,179,0,0.55), 0 0 60px rgba(255,179,0,0.2), 0 4px 20px rgba(0,0,0,0.6)",
  buttonStoppedBg: "rgba(255,255,255,0.08)",
  buttonStoppedShadow: "0 2px 12px rgba(0,0,0,0.4)",
  buttonIconActive: "#07090c",
  buttonIconActiveBorder: "rgba(7,9,12,0.25)",
  buttonIconStopped: "rgba(255,255,255,0.88)",
  // rosette geometry colours (rgba strings for SVG strokes/fills)
  petalFill: "rgba(255,179,0,0.05)",
  petalStroke: "rgba(255,179,0,0.38)",
  midDiamondFill: "rgba(255,179,0,0.07)",
  midDiamondStroke: "rgba(255,179,0,0.28)",
  outerOctaFill: "rgba(255,179,0,0.04)",
  outerOctaStroke: "rgba(255,179,0,0.22)",
  starFill: "rgba(255,179,0,0.09)",
  starStroke: "rgba(255,179,0,0.55)",
  spokeStroke: "rgba(255,179,0,0.15)",
  crossStroke: "rgba(255,179,0,0.1)",
  innerOctaFill: "rgba(255,179,0,0.1)",
  innerOctaStroke: "rgba(255,179,0,0.35)",
  centerFill: "rgba(255,179,0,0.14)",
  centerStroke: "rgba(255,179,0,0.55)",
  outerRingStroke: "rgba(255,179,0,0.18)",
  secondRingStroke: "rgba(255,179,0,0.12)",
  auraStroke: "rgba(255,179,0,0.08)",
};

// Light mode: warm parchment background, darker gold (#b87008 = --gold in light),
// designed to sit alongside the rest of the light theme without feeling out of place.
const LIGHT_PALETTE = {
  cardBg: "linear-gradient(170deg,#fbf6ec 0%,#f5ecd6 45%,#fbf6ec 100%)",
  cardBorder: "rgba(184,112,8,0.28)",
  cardShadow: "0 8px 28px rgba(122,74,0,0.12), 0 0 0 1px rgba(184,112,8,0.08), inset 0 1px 0 rgba(255,255,255,0.6)",
  patternStroke: "#b87008",
  patternOpacity: 0.08,
  ambientGlow: "radial-gradient(circle, rgba(184,112,8,0.13) 0%, transparent 62%)",
  badgeBg: "rgba(255,255,255,0.65)",
  badgeBorder: "1px solid rgba(184,112,8,0.18)",
  badgeText: "rgba(94,56,0,0.82)",
  liveDot: "#d63b3b",
  eyebrow: "rgba(122,74,0,0.6)",
  rosetteGold: "#8a5406",
  rosetteGoldShadow: "rgba(184,112,8,0.5), 0 0 48px rgba(184,112,8,0.22)",
  rosetteShadow: "drop-shadow(0 0 14px rgba(184,112,8,0.28))",
  stripBg: "rgba(255,255,255,0.7)",
  stripBorderPlaying: "rgba(184,112,8,0.22)",
  stripBorderBuffering: "rgba(184,112,8,0.32)",
  stripBorderError: "rgba(192,52,52,0.32)",
  stripBorderStopped: "rgba(184,112,8,0.14)",
  siteLink: "rgba(122,74,0,0.85)",
  showText: "rgba(26,26,26,0.85)",
  showArtist: "rgba(122,74,0,0.85)",
  waveformGradient: "linear-gradient(to top, rgba(184,112,8,0.4), #8a5406)",
  buttonPlayingBg: "linear-gradient(135deg,#b87008 0%,#8a5406 55%,#5e3800 100%)",
  buttonPlayingShadow: "0 4px 18px rgba(122,74,0,0.45), 0 0 32px rgba(184,112,8,0.28), 0 2px 6px rgba(0,0,0,0.18)",
  buttonStoppedBg: "rgba(122,74,0,0.08)",
  buttonStoppedShadow: "0 2px 8px rgba(122,74,0,0.18), inset 0 0 0 1px rgba(184,112,8,0.18)",
  buttonIconActive: "#fbf6ec",
  buttonIconActiveBorder: "rgba(251,246,236,0.35)",
  buttonIconStopped: "rgba(94,56,0,0.85)",
  // rosette geometry — denser opacities so the geometry reads on cream
  petalFill: "rgba(184,112,8,0.08)",
  petalStroke: "rgba(122,74,0,0.5)",
  midDiamondFill: "rgba(184,112,8,0.1)",
  midDiamondStroke: "rgba(122,74,0,0.42)",
  outerOctaFill: "rgba(184,112,8,0.06)",
  outerOctaStroke: "rgba(122,74,0,0.32)",
  starFill: "rgba(184,112,8,0.12)",
  starStroke: "rgba(94,56,0,0.65)",
  spokeStroke: "rgba(122,74,0,0.28)",
  crossStroke: "rgba(122,74,0,0.2)",
  innerOctaFill: "rgba(184,112,8,0.14)",
  innerOctaStroke: "rgba(94,56,0,0.5)",
  centerFill: "rgba(184,112,8,0.18)",
  centerStroke: "rgba(94,56,0,0.65)",
  outerRingStroke: "rgba(122,74,0,0.3)",
  secondRingStroke: "rgba(122,74,0,0.2)",
  auraStroke: "rgba(184,112,8,0.1)",
};

function Rosette({ sz = 210, palette }) {
  const c = sz / 2;
  const Ro = sz * 0.465;
  const Rs = sz * 0.385;
  const Ri = sz * 0.155;
  const Roc = sz * 0.255;
  const Ric = sz * 0.118;
  const Rp = sz * 0.498;

  const pt = (deg, r) => {
    const a = (deg - 90) * Math.PI / 180;
    return [c + Math.cos(a) * r, c + Math.sin(a) * r];
  };

  const starPts = Array.from({ length: 16 }, (_, i) => {
    const [x, y] = pt(i * 22.5, i % 2 === 0 ? Rs : Ri);
    return `${x},${y}`;
  }).join(" ");

  const octPts = Array.from({ length: 8 }, (_, i) => {
    const [x, y] = pt(i * 45, Roc);
    return `${x},${y}`;
  }).join(" ");

  const innerOctPts = Array.from({ length: 8 }, (_, i) => {
    const [x, y] = pt(i * 45 + 22.5, Ric * 1.5);
    return `${x},${y}`;
  }).join(" ");

  const petalPaths = Array.from({ length: 8 }, (_, i) => {
    const [tx, ty] = pt(i * 45, Ro);
    const [lx, ly] = pt(i * 45 + 22.5, Rs);
    const [rx, ry] = pt(i * 45 - 22.5, Rs);
    return `M ${tx},${ty} L ${lx},${ly} L ${c},${c} L ${rx},${ry} Z`;
  });

  const midDiamondPaths = Array.from({ length: 8 }, (_, i) => {
    const angle = i * 45 + 22.5;
    const dr = sz * 0.055;
    const [tx, ty] = pt(angle, Ro * 0.76 + dr);
    const [bx, by] = pt(angle, Ro * 0.76 - dr);
    const [lx, ly] = pt(angle + 90, Ro * 0.76 - sz * 0.01);
    const [rx, ry] = pt(angle - 90, Ro * 0.76 - sz * 0.01);
    return `M ${tx},${ty} L ${lx},${ly} L ${bx},${by} L ${rx},${ry} Z`;
  });

  const spokes = Array.from({ length: 8 }, (_, i) => {
    const [x1, y1] = pt(i * 45, Ri * 0.85);
    const [x2, y2] = pt(i * 45, Rs);
    return { x1, y1, x2, y2 };
  });

  const crosses = Array.from({ length: 4 }, (_, i) => {
    const [x1, y1] = pt(i * 45, Rs);
    const [x2, y2] = pt(i * 45 + 180, Rs);
    return { x1, y1, x2, y2 };
  });

  return (
    <svg
      width={sz}
      height={sz}
      viewBox={`0 0 ${sz} ${sz}`}
      style={{
        filter: palette.rosetteShadow,
        animation: "geomPulse 6s ease-in-out infinite",
      }}
      aria-hidden
    >
      <circle cx={c} cy={c} r={Rp} fill="none"
        stroke={palette.outerRingStroke} strokeWidth="0.7" strokeDasharray="3 6" />
      <circle cx={c} cy={c} r={Ro * 1.02} fill="none"
        stroke={palette.secondRingStroke} strokeWidth="0.6" />
      {petalPaths.map((d, i) => (
        <path key={`p${i}`} d={d}
          fill={palette.petalFill}
          stroke={palette.petalStroke} strokeWidth="0.75" strokeLinejoin="round" />
      ))}
      {midDiamondPaths.map((d, i) => (
        <path key={`m${i}`} d={d}
          fill={palette.midDiamondFill}
          stroke={palette.midDiamondStroke} strokeWidth="0.6" />
      ))}
      <polygon points={octPts}
        fill={palette.outerOctaFill}
        stroke={palette.outerOctaStroke} strokeWidth="0.7" />
      <polygon points={starPts}
        fill={palette.starFill}
        stroke={palette.starStroke} strokeWidth="0.9" />
      {spokes.map((l, i) => (
        <line key={`s${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke={palette.spokeStroke} strokeWidth="0.5" />
      ))}
      {crosses.map((l, i) => (
        <line key={`c${i}`} x1={l.x1} y1={l.y1} x2={l.x2} y2={l.y2}
          stroke={palette.crossStroke} strokeWidth="0.5" />
      ))}
      <polygon points={innerOctPts}
        fill={palette.innerOctaFill}
        stroke={palette.innerOctaStroke} strokeWidth="0.75" />
      <circle cx={c} cy={c} r={Ric}
        fill={palette.centerFill}
        stroke={palette.centerStroke} strokeWidth="1" />
      <circle cx={c} cy={c} r={Rp * 0.82} fill="none"
        stroke={palette.auraStroke} strokeWidth="20" />
    </svg>
  );
}

function BackgroundPattern({ palette }) {
  const cells = Array.from({ length: 8 }, (_, i) => {
    const a1 = (i * 45 - 90) * Math.PI / 180;
    const a2 = (i * 45 + 22.5 - 90) * Math.PI / 180;
    const a3 = ((i + 1) * 45 - 90) * Math.PI / 180;
    const R = 22, r = 9, cx = 30, cy = 30;
    const x1 = cx + Math.cos(a1) * R, y1 = cy + Math.sin(a1) * R;
    const x2 = cx + Math.cos(a2) * r, y2 = cy + Math.sin(a2) * r;
    const x3 = cx + Math.cos(a3) * R, y3 = cy + Math.sin(a3) * R;
    return <polygon key={i} points={`${cx},${cy} ${x1},${y1} ${x2},${y2} ${x3},${y3}`} />;
  });
  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", opacity: palette.patternOpacity }} aria-hidden>
      <svg width="100%" height="100%" viewBox="0 0 120 120" preserveAspectRatio="xMidYMid slice">
        <defs>
          <pattern id="rhc-bg8" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <g fill="none" stroke={palette.patternStroke} strokeWidth="0.5">{cells}</g>
          </pattern>
        </defs>
        <rect width="120" height="120" fill="url(#rhc-bg8)" />
      </svg>
    </div>
  );
}

function Waveform({ palette }) {
  const h = [11, 20, 9, 22, 13, 7, 19, 11, 22, 8];
  const d = [0, 0.22, 0.08, 0.34, 0.14, 0.26, 0.05, 0.42, 0.16, 0.48];
  const t = [1.1, 0.8, 1.3, 0.92, 1.08, 0.86, 1.35, 0.74, 1.06, 1.2];
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 22 }} aria-hidden>
      {h.map((bh, i) => (
        <span key={i} style={{
          display: "block", width: 3, height: bh, borderRadius: "3px 3px 1px 1px",
          background: palette.waveformGradient,
          transformOrigin: "bottom center",
          animation: `wv ${t[i]}s cubic-bezier(.4,0,.2,1) ${d[i]}s infinite`,
        }} />
      ))}
    </div>
  );
}

function LiveBadge({ playing, palette }) {
  return (
    <div style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: palette.badgeBg, backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      border: palette.badgeBorder, borderRadius: 20, padding: "5px 11px",
    }}>
      <div style={{
        width: 7, height: 7, borderRadius: "50%", background: palette.liveDot,
        animation: "liveDot 1.8s ease-in-out infinite",
      }} />
      <span style={{
        fontFamily: MONO, fontSize: 9, fontWeight: 700,
        color: palette.badgeText, letterSpacing: 2,
      }}>{playing ? "ON AIR" : "READY"}</span>
    </div>
  );
}

export default function RadioHeroCard({
  station,
  playbackState = "stopped",
  nowPlaying = null,
  autoplayBlocked = false,
  onPlayPause = () => {},
  onSiteOpen = null,
  darkMode = true,
}) {
  const palette = darkMode ? DARK_PALETTE : LIGHT_PALETTE;
  const isPlaying = playbackState === "playing";
  const isBuffering = playbackState === "buffering";
  const isError = playbackState === "error";

  const stripBorder = isPlaying
    ? palette.stripBorderPlaying
    : isBuffering
    ? palette.stripBorderBuffering
    : isError
    ? palette.stripBorderError
    : palette.stripBorderStopped;

  const buttonBg = isPlaying || isBuffering ? palette.buttonPlayingBg : palette.buttonStoppedBg;
  const buttonShadow = isPlaying || isBuffering ? palette.buttonPlayingShadow : palette.buttonStoppedShadow;

  return (
    <div style={{
      margin: "12px 14px", borderRadius: 26, overflow: "hidden", position: "relative",
      background: palette.cardBg,
      border: `1px solid ${palette.cardBorder}`,
      boxShadow: palette.cardShadow,
    }}>
      <BackgroundPattern palette={palette} />

      <div style={{
        position: "absolute", top: "8%", left: "50%", transform: "translateX(-50%)",
        width: 280, height: 280, borderRadius: "50%", pointerEvents: "none",
        background: palette.ambientGlow,
        animation: "geomPulse 7s ease-in-out infinite",
      }} aria-hidden />

      <div style={{
        position: "relative", zIndex: 2,
        padding: "18px 20px 0",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
      }}>
        <LiveBadge playing={isPlaying || isBuffering} palette={palette} />
        <span style={{
          fontFamily: MONO, fontSize: 8.5, fontWeight: 700,
          color: palette.eyebrow, letterSpacing: 2.5, textTransform: "uppercase",
        }}>{station?.url?.includes("livemasjid.com") ? "Masjid Live" : "Islamic Radio"}</span>
      </div>

      <div style={{
        position: "relative", zIndex: 2,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "14px 0 6px",
        animation: "subtleFloat 7s ease-in-out infinite",
      }}>
        <div style={{ position: "relative" }}>
          <Rosette sz={210} palette={palette} />
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            pointerEvents: "none",
          }}>
            <span style={{
              fontFamily: ARABIC,
              fontSize: 52, fontWeight: 700,
              color: palette.rosetteGold,
              textShadow: `0 0 24px ${palette.rosetteGoldShadow}`,
              lineHeight: 1, userSelect: "none",
            }}>{station?.arabicInitial || "م"}</span>
          </div>
        </div>
      </div>

      <div style={{
        position: "relative", zIndex: 2,
        margin: "2px 12px 12px",
        background: palette.stripBg,
        backdropFilter: "blur(24px)", WebkitBackdropFilter: "blur(24px)",
        border: `1px solid ${stripBorder}`,
        borderRadius: 18, padding: "14px 18px",
        transition: "border-color .3s ease",
      }}>
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
          fontFamily: MONO, fontSize: 8.5, fontWeight: 700,
          color: "var(--muted)", letterSpacing: 2.5, textTransform: "uppercase", marginBottom: 5,
        }}>
          <span>Now Tuned</span>
          {station?.websiteUrl && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSiteOpen ? onSiteOpen() : window.open(station.websiteUrl, "_blank", "noopener,noreferrer"); }}
              style={{
                background: "transparent", border: "none", color: palette.siteLink,
                fontFamily: MONO, fontSize: 8.5, fontWeight: 700, letterSpacing: 1.8,
                cursor: "pointer", padding: 0,
              }}
            >↗ SITE</button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: SERIF, fontStyle: "italic",
              fontSize: 32, color: "var(--text)", lineHeight: 1.04, letterSpacing: "-0.025em",
              marginBottom: 8,
              overflowWrap: "normal", wordBreak: "normal", hyphens: "none",
            }}>{station?.name || "—"}</div>

            {isPlaying && nowPlaying?.show ? (
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Waveform palette={palette} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    fontSize: 11, fontWeight: 600, color: palette.showText,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: 1.2,
                  }}>{nowPlaying.show}</div>
                  {nowPlaying.artist && (
                    <div style={{
                      fontFamily: MONO, fontSize: 9, color: palette.showArtist,
                      letterSpacing: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{nowPlaying.artist}</div>
                  )}
                </div>
              </div>
            ) : isPlaying ? (
              <Waveform palette={palette} />
            ) : isBuffering ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8, height: 22 }}>
                <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
                <span style={{
                  fontFamily: MONO, fontSize: 9.5, color: "var(--gold)", letterSpacing: 1.5,
                }}>CONNECTING…</span>
              </div>
            ) : isError ? (
              <span style={{
                fontFamily: MONO, fontSize: 9.5, color: "var(--danger)", letterSpacing: 1.5,
              }}>{autoplayBlocked ? "TAP TO START" : "STREAM OFFLINE — TAP RETRY"}</span>
            ) : (
              <span style={{
                fontFamily: MONO, fontSize: 9.5, color: "var(--muted)", letterSpacing: 1.5,
              }}>ON THE AIR — TAP TO TUNE IN</span>
            )}
          </div>

          <button
            type="button"
            onClick={onPlayPause}
            aria-label={isPlaying ? "Stop" : isBuffering ? "Cancel" : "Play"}
            style={{
              width: 52, height: 52, borderRadius: "50%", border: "none", cursor: "pointer", flexShrink: 0,
              background: buttonBg,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: buttonShadow,
              transition: "all .3s cubic-bezier(.4,0,.2,1)",
              padding: 0,
            }}
          >
            {isBuffering ? (
              <span className="spinner" style={{ width: 20, height: 20, borderWidth: 2, borderStyle: "solid", borderColor: palette.buttonIconActiveBorder, borderTopColor: palette.buttonIconActive }} />
            ) : isPlaying ? (
              <svg width="16" height="16" fill={palette.buttonIconActive} viewBox="0 0 24 24" aria-hidden>
                <rect x="6" y="4" width="4" height="16" rx="2" />
                <rect x="14" y="4" width="4" height="16" rx="2" />
              </svg>
            ) : (
              <svg width="18" height="18" fill={palette.buttonIconStopped} viewBox="0 0 24 24" aria-hidden>
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
